import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { get, post, put, del } from "@/services/ApiService";
import {
  GET_CART,
  ADD_TO_CART,
  CLEAR_CART,
  UPDATE_CART_ITEM,
  REMOVE_CART_ITEM,
} from "@/services/ApiRoutes";
import { CartItem, CartState } from "@/types/cart";
import { Product } from "@/types/shop";

// ─── LocalStorage Guest Cart Helpers ──────────────────────────────────────────

const GUEST_CART_KEY = "hubmicroo_guest_cart";

function calculateCartTotals(items: CartItem[]) {
  let total = 0;
  let itemCount = 0;
  for (const item of items) {
    const price =
      item.product?.discountPrice && item.product.discountPrice > 0 && item.product.discountPrice < item.product.price
        ? item.product.discountPrice
        : item.product?.price || 0;
    total += price * (item.quantity || 1);
    itemCount += item.quantity || 1;
  }
  return { total: Math.round(total * 100) / 100, itemCount };
}

function loadGuestCart(): { items: CartItem[]; total: number; itemCount: number } {
  if (typeof window === "undefined") return { items: [], total: 0, itemCount: 0 };
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY);
    if (!raw) return { items: [], total: 0, itemCount: 0 };
    const parsed = JSON.parse(raw);
    const items: CartItem[] = Array.isArray(parsed.items) ? parsed.items : [];
    const { total, itemCount } = calculateCartTotals(items);
    return { items, total, itemCount };
  } catch (e) {
    return { items: [], total: 0, itemCount: 0 };
  }
}

function saveGuestCart(items: CartItem[]): { items: CartItem[]; total: number; itemCount: number } {
  const { total, itemCount } = calculateCartTotals(items);
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify({ items, total, itemCount }));
    } catch (e) {
      console.error("Failed to save guest cart to localStorage", e);
    }
  }
  return { items, total, itemCount };
}

function clearGuestCart() {
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(GUEST_CART_KEY);
    } catch (e) {}
  }
}

// ─── Helper — normalise API cart response into state ─────────────────────────

function applyCartResponse(state: CartState, data: any) {
  if (!data) return;
  state.cartId = data._id ?? null;
  state.items = Array.isArray(data.items) ? data.items : [];
  state.total = data.total ?? 0;
  state.itemCount = data.itemCount ?? 0;
  // If backend returns items, keep guest storage in sync
  saveGuestCart(state.items);
}

// ─── Initial state ────────────────────────────────────────────────────────────

const guestInitial = loadGuestCart();

const initialState: CartState = {
  cartId: null,
  items: guestInitial.items,
  total: guestInitial.total,
  itemCount: guestInitial.itemCount,
  loading: false,
  addingToCart: false,
  updatingItemId: null,
  removingItemId: null,
  error: null,
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

// GET /api/v1/cart
export const fetchCartAction = createAsyncThunk(
  "cart/fetchCart",
  async (_, { rejectWithValue }) => {
    const hasToken = typeof window !== "undefined" && !!localStorage.getItem("token");
    if (!hasToken) {
      return loadGuestCart();
    }
    try {
      const res = await get(GET_CART);
      return res.data?.data ?? res.data;
    } catch (err: any) {
      // If unauthorized or error, fallback to local guest cart
      return loadGuestCart();
    }
  }
);

// POST /api/v1/cart  body: { productId, quantity, product?, selectedSize?, selectedColor?, selectedVariant? }
export const addToCartAction = createAsyncThunk(
  "cart/addToCart",
  async (
    payload: {
      productId: string;
      quantity: number;
      product?: Product;
      selectedSize?: string;
      selectedColor?: string;
      selectedVariant?: string;
    },
    { rejectWithValue }
  ) => {
    const hasToken = typeof window !== "undefined" && !!localStorage.getItem("token");

    if (hasToken) {
      try {
        const res = await post(ADD_TO_CART, {
          productId: payload.productId,
          quantity: payload.quantity,
          selectedSize: payload.selectedSize,
          selectedColor: payload.selectedColor,
          selectedVariant: payload.selectedVariant,
        });
        return res.data?.data ?? res.data;
      } catch (err: any) {
        // If API fails with 401 or network error, fallback to guest cart below
      }
    }

    // ── Local Guest Cart Fallback ──
    const guestCart = loadGuestCart();
    const existingIndex = guestCart.items.findIndex(
      (item) =>
        (item.product?._id === payload.productId || (item.product as any)?.id === payload.productId) &&
        (item.selectedSize || "") === (payload.selectedSize || "") &&
        (item.selectedColor || "") === (payload.selectedColor || "") &&
        (item.selectedVariant || "") === (payload.selectedVariant || "")
    );

    let updatedItems = [...guestCart.items];

    if (existingIndex > -1) {
      const current = updatedItems[existingIndex];
      const maxStock = current.product?.stock ?? 99;
      const newQty = Math.min(maxStock, current.quantity + (payload.quantity || 1));
      updatedItems[existingIndex] = {
        ...current,
        quantity: newQty,
      };
    } else {
      const newItem: CartItem = {
        _id: `guest-item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        product:
          payload.product ||
          ({
            _id: payload.productId,
            name: "Product",
            price: 0,
            images: [],
          } as unknown as Product),
        quantity: payload.quantity || 1,
        selectedSize: payload.selectedSize || "",
        selectedColor: payload.selectedColor || "",
        selectedVariant: payload.selectedVariant || "",
        addedAt: new Date().toISOString(),
      };
      updatedItems.push(newItem);
    }

    return saveGuestCart(updatedItems);
  }
);

// PUT /api/v1/cart/:cartItemId  body: { quantity }
export const updateCartItemAction = createAsyncThunk(
  "cart/updateCartItem",
  async (
    { cartItemId, quantity }: { cartItemId: string; quantity: number },
    { rejectWithValue }
  ) => {
    const hasToken = typeof window !== "undefined" && !!localStorage.getItem("token");

    if (hasToken && !cartItemId.startsWith("guest-")) {
      try {
        const res = await put(UPDATE_CART_ITEM(cartItemId), { quantity });
        return res.data?.data ?? res.data;
      } catch (err: any) {
        // Fallback to guest update if API fails
      }
    }

    const guestCart = loadGuestCart();
    const updatedItems = guestCart.items.map((item) =>
      item._id === cartItemId ? { ...item, quantity: Math.max(1, quantity) } : item
    );
    return saveGuestCart(updatedItems);
  }
);

// DELETE /api/v1/cart/:cartItemId
export const removeCartItemAction = createAsyncThunk(
  "cart/removeCartItem",
  async (cartItemId: string, { rejectWithValue }) => {
    const hasToken = typeof window !== "undefined" && !!localStorage.getItem("token");

    if (hasToken && !cartItemId.startsWith("guest-")) {
      try {
        const res = await del(REMOVE_CART_ITEM(cartItemId));
        return res.data?.data ?? res.data;
      } catch (err: any) {
        // Fallback to guest remove if API fails
      }
    }

    const guestCart = loadGuestCart();
    const updatedItems = guestCart.items.filter((item) => item._id !== cartItemId);
    return saveGuestCart(updatedItems);
  }
);

// DELETE /api/v1/cart
export const clearCartAction = createAsyncThunk(
  "cart/clearCart",
  async (_, { rejectWithValue }) => {
    const hasToken = typeof window !== "undefined" && !!localStorage.getItem("token");

    if (hasToken) {
      try {
        await del(CLEAR_CART);
      } catch (err: any) {}
    }

    clearGuestCart();
    return { items: [], total: 0, itemCount: 0 };
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    // Sync reset — used after order placement to instantly clear local state
    resetCart: (state) => {
      state.cartId = null;
      state.items = [];
      state.total = 0;
      state.itemCount = 0;
      state.error = null;
      clearGuestCart();
    },
    clearCartError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchCart
    builder
      .addCase(fetchCartAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCartAction.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) applyCartResponse(state, action.payload);
      })
      .addCase(fetchCartAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // addToCart
    builder
      .addCase(addToCartAction.pending, (state) => {
        state.addingToCart = true;
        state.error = null;
      })
      .addCase(addToCartAction.fulfilled, (state, action) => {
        state.addingToCart = false;
        if (action.payload) applyCartResponse(state, action.payload);
      })
      .addCase(addToCartAction.rejected, (state, action) => {
        state.addingToCart = false;
        state.error = action.payload as string;
      });

    // updateCartItem
    builder
      .addCase(updateCartItemAction.pending, (state, action) => {
        state.updatingItemId = action.meta.arg.cartItemId;
        state.error = null;
      })
      .addCase(updateCartItemAction.fulfilled, (state, action) => {
        state.updatingItemId = null;
        if (action.payload) applyCartResponse(state, action.payload);
      })
      .addCase(updateCartItemAction.rejected, (state, action) => {
        state.updatingItemId = null;
        state.error = action.payload as string;
      });

    // removeCartItem
    builder
      .addCase(removeCartItemAction.pending, (state, action) => {
        state.removingItemId = action.meta.arg;
        state.error = null;
      })
      .addCase(removeCartItemAction.fulfilled, (state, action) => {
        state.removingItemId = null;
        if (action.payload) applyCartResponse(state, action.payload);
        else {
          state.items = [];
          state.total = 0;
          state.itemCount = 0;
        }
      })
      .addCase(removeCartItemAction.rejected, (state, action) => {
        state.removingItemId = null;
        state.error = action.payload as string;
      });

    // clearCart
    builder
      .addCase(clearCartAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearCartAction.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          applyCartResponse(state, action.payload);
        } else {
          state.cartId = null;
          state.items = [];
          state.total = 0;
          state.itemCount = 0;
        }
      })
      .addCase(clearCartAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetCart, clearCartError } = cartSlice.actions;
export default cartSlice.reducer;
